import { DomSanitizer } from '@angular/platform-browser';
import { DriveStep, Driver, PopoverDOM } from 'driver.js';
import { Tour, TourColorTheme, TourPopoverSide, TourStep } from '@pncl/common-models';
import { InstitutionTheme } from 'src/app/models/user';
import { buildTourStepHtml } from './tour-content';
import {
  isElementDisplayed,
  resolveTourStepElement,
  useNearestScrollIntoViewForNextCall,
} from './tour-dom';

const TOUR_BACKDROP_COLOR = 'rgba(51, 51, 51, 0.7)';
const TOUR_BACKDROP_OPACITY = 1;

// Build the base Driver.js config from the first step's navigation and backdrop state.
export function getDriverConfig(tour: Tour, stepsLength: number) {
  const firstStep = tour.steps[0];
  const showDismissButton = shouldShowDismissButton(tour);

  return {
    animate: true,
    smoothScroll: true,
    allowClose: showDismissButton,
    showButtons: getPopoverButtonsForStep(tour, 0, showDismissButton),
    nextBtnText: 'Next',
    doneBtnText: 'Done',
    showProgress: stepsLength > 1,
    progressText: '{{current}} of {{total}}',
    popoverClass: 'pencil-tour-popover',
    popoverOffset: getPopoverOffsetForStep(firstStep),
    overlayColor: TOUR_BACKDROP_COLOR,
    overlayOpacity: getOverlayOpacityForStep(firstStep),
  };
}

// Driver.js needs per-step config updates as the active highlight changes.
export function getActiveStepDriverConfig(tour: Tour, step?: TourStep) {
  return {
    allowClose: shouldShowDismissButton(tour),
    overlayColor: TOUR_BACKDROP_COLOR,
    overlayOpacity: getOverlayOpacityForStep(step),
    popoverOffset: getPopoverOffsetForStep(step),
  };
}

// Translate Pencil's step model into the popover config Driver.js expects.
export function getStepPopoverConfig(params: {
  tour: Tour;
  step: TourStep;
  stepIndex: number;
  currentPath: string;
  userTheme?: string;
  sanitizer: DomSanitizer;
}) {
  const isMultiStepTour = params.tour.steps.length > 1;
  const showDismissButton = shouldShowDismissButton(params.tour);

  return {
    title: undefined,
    description: buildTourStepHtml(
      params.step.popoverContent,
      params.sanitizer,
      params.step.popoverStyle?.customClass,
    ),
    popoverClass: getStepPopoverClass(
      params.tour,
      params.step,
      params.currentPath,
      params.userTheme,
    ),
    showButtons: getPopoverButtonsForStep(params.tour, params.stepIndex, showDismissButton),
    prevBtnText: showDismissButton && isMultiStepTour ? 'Skip' : 'Back',
    side: params.step.popoverStyle?.side,
    align: params.step.popoverStyle?.align,
  };
}

// Convert the tour definition into Driver.js steps and stop early if any target is missing.
export function buildDriverSteps(params: {
  tour: Tour;
  currentPath: string;
  userTheme?: string;
  sanitizer: DomSanitizer;
  onHighlightStarted: (step: TourStep, driverInstance: Driver) => void;
  onNextClick: (stepIndex: number, driverInstance: Driver) => void;
  onPrevClick: (stepIndex: number, showDismissButton: boolean, driverInstance: Driver) => void;
}): DriveStep[] {
  const steps: DriveStep[] = [];

  for (const [index, step] of params.tour.steps.entries()) {
    const target = resolveTourStepElement(step.element);

    if (!target) {
      return [];
    }

    const showDismissButton = shouldShowDismissButton(params.tour);
    const popoverConfig = getStepPopoverConfig({
      tour: params.tour,
      step,
      stepIndex: index,
      currentPath: params.currentPath,
      userTheme: params.userTheme,
      sanitizer: params.sanitizer,
    });

    steps.push({
      // Resolve on step entry so a breakpoint change can replace the validated target.
      element: () => resolveTourStepElement(step.element, target) ?? target,
      onHighlightStarted: (element, _driverStep, options) => {
        if (element) {
          useNearestScrollIntoViewForNextCall(element);
        }

        params.onHighlightStarted(step, options.driver);
      },
      popover: {
        ...popoverConfig,
        onNextClick: (_element, _driverStep, options) => {
          params.onNextClick(index, options.driver);
        },
        onPrevClick: (_element, _driverStep, options) => {
          params.onPrevClick(index, showDismissButton, options.driver);
        },
      },
    });
  }

  return steps;
}

// Popover dismiss UI is controlled only by the tour-level `dismissal` config.
export function shouldShowDismissButton(tour: Tour): boolean {
  return tour.dismissal?.showDismissButton ?? false;
}

// Each step can opt out of the backdrop while still using the shared default opacity.
export function getOverlayOpacityForStep(step?: TourStep): number {
  return step?.popoverStyle?.showBackdrop === false ? 0 : TOUR_BACKDROP_OPACITY;
}

// Driver.js renders the popover DOM, but Pencil still needs to normalize its buttons and theme.
export function syncRenderedPopoverStyling(params: {
  tour: Tour;
  activeStep?: TourStep;
  popover: PopoverDOM;
  institutionTheme?: InstitutionTheme;
}): void {
  syncDismissButtonState(params.tour, params.activeStep, params.popover);
  syncPopoverLayoutState(params.popover.wrapper);
  applyInstitutionTheme(params.popover.wrapper, params.institutionTheme);
}

// Popover classes combine the resolved theme with any step-specific custom class.
function getStepPopoverClass(
  tour: Tour,
  step: TourStep | undefined,
  currentPath: string,
  userTheme?: string,
): string {
  const theme = getTourColorTheme(tour, currentPath, userTheme);
  const base = `pencil-tour-popover pencil-tour-popover--${theme}`;
  const custom = step?.popoverStyle?.customClass;
  return custom ? `${base} ${custom}`.trim() : base;
}

// Theme choice prefers explicit tour config, then the current user/theme context, then a product default.
function getTourColorTheme(tour: Tour, currentPath: string, userTheme?: string): TourColorTheme {
  if (tour.theme === TourColorTheme.DARK) {
    return TourColorTheme.DARK;
  }

  if (tour.theme === TourColorTheme.LIGHT) {
    return TourColorTheme.LIGHT;
  }

  if (userTheme === TourColorTheme.DARK || userTheme === TourColorTheme.LIGHT) {
    return userTheme as TourColorTheme;
  }

  return TourColorTheme.DARK;
}

// Driver.js offsets are scalar, so map Pencil's axis offsets onto the active popover side.
function getPopoverOffsetForStep(step?: TourStep): number {
  const styling = step?.popoverStyle;

  if (!styling) {
    return 0;
  }

  if (styling.side === TourPopoverSide.TOP || styling.side === TourPopoverSide.BOTTOM) {
    return Math.abs(styling.offsetY ?? 0);
  }

  if (styling.side === TourPopoverSide.LEFT || styling.side === TourPopoverSide.RIGHT) {
    return Math.abs(styling.offsetX ?? 0);
  }

  return 0;
}

// Button visibility is derived from step position plus Pencil's dismiss/skip behavior.
function getPopoverButtonsForStep(
  tour: Tour,
  stepIndex: number,
  showDismissButton: boolean,
): Array<'previous' | 'next' | 'close'> {
  const isMultiStepTour = tour.steps.length > 1;
  const isLastStep = stepIndex === tour.steps.length - 1;

  if (!isMultiStepTour) {
    return showDismissButton ? ['next'] : [];
  }

  if (stepIndex === 0) {
    return showDismissButton ? ['previous', 'next'] : ['next'];
  }

  if (showDismissButton) {
    return isLastStep ? ['next'] : ['previous', 'next'];
  }

  return ['previous', 'next'];
}

// Driver.js defaults do not match Pencil's dismiss and skip affordances, so patch them after render.
function syncDismissButtonState(
  tour: Tour,
  activeStep: TourStep | undefined,
  popover: Pick<PopoverDOM, 'closeButton' | 'previousButton'>,
): void {
  const previousButton = popover.previousButton;
  const closeButton = popover.closeButton;

  if (closeButton) {
    closeButton.style.display = 'none';
    closeButton.hidden = true;
    closeButton.tabIndex = -1;
    closeButton.setAttribute('aria-hidden', 'true');
  }

  if (!previousButton) {
    return;
  }

  const stepIndex = activeStep ? tour.steps.indexOf(activeStep) : -1;
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === tour.steps.length - 1;
  const isMultiStepTour = tour.steps.length > 1;
  const showDismissButton = shouldShowDismissButton(tour);

  if (!isMultiStepTour || stepIndex < 0) {
    previousButton.style.display = 'none';
    return;
  }

  if (isLastStep && showDismissButton) {
    previousButton.style.display = 'none';
    return;
  }

  if (isFirstStep && !showDismissButton) {
    previousButton.style.display = 'none';
    return;
  }

  previousButton.style.display = '';

  if (isFirstStep && showDismissButton) {
    previousButton.disabled = false;
    previousButton.removeAttribute('disabled');
    previousButton.removeAttribute('aria-disabled');
    previousButton.classList.remove('driver-popover-btn-disabled');
  }
}

// CTA-specific layout styling only makes sense when at least one nav button is visible.
function syncPopoverLayoutState(wrapper: HTMLElement): void {
  const navigationButtons = wrapper.querySelector<HTMLElement>('.driver-popover-navigation-btns');

  if (!navigationButtons) {
    wrapper.classList.remove('pencil-tour-popover--has-cta');
    return;
  }

  const hasVisibleCta = Array.from(
    navigationButtons.querySelectorAll<HTMLButtonElement>('button'),
  ).some((button) => isElementDisplayed(button));

  wrapper.classList.toggle('pencil-tour-popover--has-cta', hasVisibleCta);
}

// Institution themes expose CSS variables that the popover can consume directly.
function applyInstitutionTheme(container: HTMLElement, institutionTheme?: InstitutionTheme): void {
  Object.entries(institutionTheme?.colors ?? {}).forEach(([key, value]) => {
    container.style.setProperty(key, value);
  });
}
